import { Icon, Typography } from "@/components/atoms";
import { clsx } from "@/utils";

/**
 * The ↳ hanging the flight field off the airport address above it.
 *
 * Drawn rather than iconed: no corner-arrow exists in this project's `IconSax` registry or in
 * `CustomIcon`, and an unregistered name renders NOTHING while logging "Icon … not found".
 *
 * Its geometry is borrowed from the address input it descends from, so the two line up rather than
 * merely sitting near each other (`BookingLocationInput`):
 *
 *   - 40px wide — that input's icon gutter (`pl-10`), so the flight box's left edge starts exactly where
 *     the address TEXT above it does.
 *   - The vertical stroke at x=22 — the centre of that input's own leading icon (`px-3` + a 20px `md`
 *     icon = a centre at 22), so the elbow drops out of the dot rather than out of open space.
 *   - 36px tall, the field's height, so the arrowhead lands on its vertical centre.
 */
const Connector = () => (
  <svg width="40" height="36" viewBox="0 0 40 36" fill="none" aria-hidden className="shrink-0 text-neutral-silver-gray">
    <path d="M22 4v9a5 5 0 0 0 5 5h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="m29.5 14.5 3.5 3.5-3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

interface FlightNumberFieldProps {
  /**
   * The captured number. Non-nullable BY DESIGN (Jodie, 18 Sep 2026): a stop with no flight hides this
   * row entirely rather than showing an empty field, so there is no empty state to render and the
   * caller's own guard is what keeps that true. See the call sites in `LocationPanel`.
   */
  flightNumber: string;
  /** Opens the Flight Details modal. The whole box is the hit area. */
  onClick: () => void;
  /**
   * Names the LEG for assistive tech — "Pickup flight number", "Drop-off flight number". Required
   * rather than defaulted because an airport-to-airport booking renders two of these, and the text
   * link they replace named them identically ("Add flight details" twice), leaving a screen reader no
   * way to tell which flight it was about to edit.
   */
  label: string;
}

/**
 * FLIGHT NUMBER — the Address step's flight field, one per airport stop (DSN, "make the flight input
 * more prominent").
 *
 * A BUTTON wearing an input's clothes, which is the whole subtlety here. It looks like a field because
 * the design promotes it to one — full width, bordered, a 24px plane in its own divided cell — but the
 * number is captured in the Flight Details modal, not typed in place, so a real `<input>` would take
 * focus and a caret and then refuse every keystroke. Nothing is editable here; the press opens the modal.
 *
 * Deliberately NOT built on `AddOnTextInput`, whose start-add-on idiom this borrows: that atom's add-on
 * is `bg-white` where this cell is filled, and its child is an `Input`. What is copied is the seam —
 * add-on rounded on one side with the shared border collapsed between the two cells — so the two read
 * as the same control family.
 *
 * Height is padding-driven, per the design's `H Hug 36px`: 6px of padding around either a 24px icon or
 * a 24px line box comes to 36px in both cells, so neither can drift from the other or from the spec if
 * the icon size changes.
 *
 * View-only needs no prop. The panel wraps its inputs in a disabled `<fieldset>` (`ViewOnlyFieldset`),
 * which disables descendant buttons natively — hence the `disabled:` variants below rather than a
 * `viewOnly` flag this component would have to be told about.
 */
export const FlightNumberField = ({ flightNumber, onClick, label }: FlightNumberFieldProps) => (
  // `items-start`, not `items-stretch`: the connector carries its own 36px and stretching would scale
  // the drawing. Both children come to 36px on their own, so they align without being made to.
  <div className="flex items-start">
    <Connector />
    <button
      type="button"
      onClick={onClick}
      // The value belongs IN the name — "Pickup flight number QF114" — so a reader hears which flight
      // they are about to change, not just which slot.
      aria-label={`${label} ${flightNumber}`}
      className={clsx(
        // `group` so the cells below can style off this button's own disabled state. They must: the
        // value and the icon set their own colours, so a `disabled:text-*` here could never reach them,
        // and a read-only booking greyed its two address inputs while leaving this one black —
        // the one control that could NOT be touched looking like the only live thing on the card.
        "group flex min-w-0 flex-1 items-stretch overflow-hidden rounded-lg border border-neutral-mid-gray bg-white text-left",
        // `enabled:hover` — CSS `:hover` still matches a disabled button, so an unqualified rule lit the
        // border primary on a booking nobody can edit, promising an interaction that isn't on offer.
        "focus-visible:border-primary focus-visible:shadow-input focus-visible:outline-none enabled:hover:border-primary",
        // The address inputs' own view-only treatment (`BookingLocationInput`), so a closed booking greys
        // the whole Address card uniformly rather than leaving this one box looking pressable.
        "disabled:cursor-not-allowed disabled:border-neutral-mid-gray disabled:bg-neutral-surface-gray"
      )}
    >
      <span className="flex items-center border-r border-neutral-mid-gray bg-neutral-surface-gray px-2.5 py-1.5">
        <Icon name="Airplane" size="lg" variant="Linear" className="text-neutral-dark-gray group-disabled:text-neutral-silver-gray" />
      </span>
      <span className="flex min-w-0 flex-1 items-center px-3 py-1.5">
        {/* `leading-6` pins the line box to 24px so the text cell measures the same 36px the icon cell
            does — `muted` is 14px whose own line-height would leave the two a pixel or two apart.

            The `group-disabled:` colour wins over the base one because Tailwind emits variant utilities
            after unprefixed ones, so this needs no `!` to beat the black beside it. */}
        <Typography variant="muted" className="truncate leading-6 text-neutral-black group-disabled:text-neutral-silver-gray">
          {flightNumber}
        </Typography>
      </span>
    </button>
  </div>
);
